// src/components/AudioPlayer.jsx
// Drop this anywhere inside ScriptureAnswer — desktop and mobile both

import React from "react";
import useSpeech from "../hooks/useSpeech";

const SPEEDS = [
  { label: "0.75x", value: 0.75 },
  { label: "1x",    value: 1.0  },
  { label: "1.25x", value: 1.25 },
    { label: "1.5x",  value: 1.5  },
];

export default function AudioPlayer({ text, isMobile = false, compact = false }) {
  const {
    isPlaying,
    isPaused,
    isSupported,
    progress,
    rate,
    play,
    pause,
    stop,
    changeRate,
  } = useSpeech(text);

  // Don't render if browser doesn't support TTS
  if (!isSupported) return null;

  // ── Styles ────────────────────────────────────────────────────────
  const containerStyle = {
    display:        "flex",
    alignItems:     "center",
    gap:            compact ? (isMobile ? "7px" : "10px") : (isMobile ? "8px" : "12px"),
    padding:        compact ? (isMobile ? "8px 12px" : "10px 16px") : (isMobile ? "10px 14px" : "12px 20px"),
    marginBottom:   compact ? (isMobile ? "12px" : "14px") : (isMobile ? "16px" : "20px"),
    background:     "linear-gradient(135deg, rgba(97,218,251,0.08) 0%, rgba(33,161,241,0.08) 100%)",
    border:         compact ? "1px solid rgba(97,218,251,0.16)" : "1px solid rgba(97,218,251,0.2)",
    borderRadius:   compact ? (isMobile ? "10px" : "14px") : (isMobile ? "12px" : "16px"),
    backdropFilter: "blur(8px)",
    flexWrap:       isMobile ? "wrap" : "nowrap",
  };

  const iconBtnStyle = (active) => ({
    background:   active
      ? "linear-gradient(135deg, rgba(97,218,251,0.25) 0%, rgba(33,161,241,0.25) 100%)"
      : "rgba(97,218,251,0.08)",
    border:       "1px solid rgba(97,218,251,0.25)",
    borderRadius: "50%",
    width:        compact ? (isMobile ? "32px" : "36px") : (isMobile ? "36px" : "40px"),
    height:       compact ? (isMobile ? "32px" : "36px") : (isMobile ? "36px" : "40px"),
    display:      "flex",
    alignItems:   "center",
    justifyContent: "center",
    cursor:       "pointer",
    fontSize:     compact ? (isMobile ? "13px" : "15px") : (isMobile ? "15px" : "17px"),
    color:        "#61dafb",
    flexShrink:   0,
    transition:   "all 0.2s ease",
  });

  const progressBarWrap = {
    flex:         1,
    minWidth:     isMobile ? "100%" : "80px",
    order:        isMobile ? 10 : 0,   // push to second line on mobile
    height:       compact ? "3px" : "4px",
    background:   "rgba(97,218,251,0.15)",
    borderRadius: "2px",
    overflow:     "hidden",
    cursor:       "default",
  };

  const progressFill = {
    height:       "100%",
    width:        `${progress}%`,
    background:   "linear-gradient(90deg, #61dafb, #21a1f1)",
    borderRadius: "2px",
    transition:   "width 0.4s ease",
  };

  const labelStyle = {
    fontSize:  compact ? (isMobile ? "10px" : "11px") : (isMobile ? "11px" : "12px"),
    color:     "#94bbe9",
    flexShrink: 0,
    whiteSpace: "nowrap",
  };

  const speedBtnStyle = (active) => ({
    background:   active ? "rgba(97,218,251,0.2)" : "transparent",
    border:       active ? "1px solid rgba(97,218,251,0.4)" : "1px solid rgba(97,218,251,0.15)",
    borderRadius: "20px",
    padding:      compact ? "2px 7px" : "3px 8px",
    cursor:       "pointer",
    fontSize:     compact ? (isMobile ? "10px" : "11px") : (isMobile ? "11px" : "12px"),
    color:        active ? "#61dafb" : "#94bbe9",
    transition:   "all 0.2s ease",
  });

  return (
    <div style={containerStyle}>

      {/* 🔊 Label */}
      <span style={{ ...labelStyle, fontSize: compact ? (isMobile ? "11px" : "12px") : (isMobile ? "12px" : "13px") }}>
        🔊 Listen
      </span>

      {/* Play / Pause button */}
      <button
        style={iconBtnStyle(isPlaying)}
        onClick={isPlaying ? pause : play}
        title={isPlaying ? "Pause" : isPaused ? "Resume" : "Play"}
      >
        {isPlaying ? "⏸" : "▶"}
      </button>

      {/* Stop button — only show when active */}
      {(isPlaying || isPaused) && (
        <button
          style={iconBtnStyle(false)}
          onClick={stop}
          title="Stop"
        >
          ⏹
        </button>
      )}

      {/* Progress bar */}
      <div style={progressBarWrap}>
        <div style={progressFill} />
      </div>

      {/* Speed controls */}
      <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
        {SPEEDS.map((s) => (
          <button
            key={s.value}
            style={speedBtnStyle(Math.abs(rate - s.value) < 0.01)}
            onClick={() => changeRate(s.value)}
            title={`Speed ${s.label}`}
          >
            {s.label}
          </button>
        ))}
      </div>

    </div>
  );
}